import { format } from 'date-fns'

import type { Appointment } from '../../osa-scheduling/types'

import type { ExportFormat } from '@/shared/utils/export.utils'
import { exportData } from '@/shared/utils/export.utils'

export { exportData }
export type { ExportFormat, ExportConfig } from '@/shared/utils/export.utils'

// Helper function to format date time
export const formatDateTime = (dateString: string, formatStr = 'MMM dd, yyyy HH:mm'): string => {
  try {
    return format(new Date(dateString), formatStr)
  } catch (error) {
    console.error('Error formatting date:', error)
    return '-'
  }
}

// Unified transform function for all appointment exports
export const transformAppointments = (appointments: Appointment[]) => {
  return appointments.map(appointment => ({
    'Customer Name': appointment.customerName || '-',
    'Customer Address': appointment.customerAddress || '-',
    'Customer Phone': appointment.customerPhone || '-',
    'Customer Email': appointment.customerEmail || '-',
    'Creation Date': appointment.createdAt ? formatDateTime(appointment.createdAt) : '-',
    'Scheduled Date': appointment.scheduleStartTime
      ? formatDateTime(appointment.scheduleStartTime)
      : '-',
    Type: transformType(appointment),
    SubType: transformSubType(appointment),
  }))
}

// Convenience functions for specific exports
export const exportUnscheduledProjects = (
  appointments: Appointment[],
  format: ExportFormat = 'xlsx'
) => {
  const data = transformAppointments(appointments)
  exportData(data, {
    filename: 'unscheduled_projects',
    sheetName: 'Unscheduled Projects',
    format,
  })
}

export const exportDailyAppointments = (
  appointments: Appointment[],
  date: Date,
  exportFormat: ExportFormat = 'xlsx'
) => {
  const data = transformAppointments(appointments)
  const dateStr = format(date, 'yyyy-MM-dd')
  exportData(data, {
    filename: `daily_appointments_${dateStr}`,
    sheetName: 'Daily Appointments',
    format: exportFormat,
  })
}

export const exportIncompleteProjects = (
  appointments: Appointment[],
  format: ExportFormat = 'xlsx'
) => {
  const data = transformAppointments(appointments)
  exportData(data, {
    filename: 'incomplete_projects',
    sheetName: 'Incomplete Projects',
    format,
  })
}

export const exportWeeklyAppointments = (
  appointments: Appointment[],
  weekRange: { start: Date; end: Date },
  exportFormat: ExportFormat = 'xlsx'
) => {
  const data = transformAppointments(appointments)
  const startStr = format(weekRange.start, 'yyyy-MM-dd')
  const endStr = format(weekRange.end, 'yyyy-MM-dd')
  exportData(data, {
    filename: `weekly_appointments_${startStr}_to_${endStr}`,
    sheetName: 'Weekly Appointments',
    format: exportFormat,
  })
}

export const exportAllAppointments = (
  appointments: Appointment[],
  exportFormat: ExportFormat = 'xlsx'
) => {
  const data = transformAppointments(appointments)
  exportData(data, {
    filename: 'all_dispatched_appointments',
    sheetName: 'All Appointments',
    format: exportFormat,
  })
}

export const transformType = (appointment: {
  type?: string | null
  scoutCreated?: boolean | null
}): string => {
  let type = appointment.type || '-'
  if (appointment.scoutCreated) {
    type = 'privat'
  }
  return type
}

export const transformSubType = (appointment: { subtypes?: string[] | null }): string => {
  if (!appointment.subtypes?.length) return '-'
  return `${appointment.subtypes?.join(',')}`
}
